import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import {
  Button,
  FlexRow,
  Tooltip,
  BodyText,
  TextInput,
  IconType,
  TDropdownElementProps,
  ButtonDropdown,
  PageLoading,
  CheckboxInput,
  DropdownItem,
} from 'ui-toolkit';
import { faCaretDown, faCaretUp, faCirclePlus, faDownload, faFolderPlus } from '@fortawesome/pro-regular-svg-icons';
import styled from 'styled-components';
import { ISelectDocumentsContainerProps } from './SelectDocumentsContainer';
import { AffidavitResultModal } from '../affidavits/AffidavitResultModal';
import { OutsideClickWrapper } from '../../overview/Summary/OutsideClickWrapper';
import { useFormikContext } from 'formik';
import { IMergeDocumentValues } from './MergeDocumentForm';
import { IStandardDocumentVersionData } from 'types/order/documents/shared/interfaces';
import { eTransactionCategory } from '../../../../ui-toolkit/enums/eTransactionType';
import { ePartyPurpose } from '../../../../ui-toolkit/enums/ePartyPurpose';
import { PineconeThemeProvider } from '../../../../ui-toolkit/theming/PineconeThemeProvider';

interface ISelectDocumentsProps extends ISelectDocumentsContainerProps {
  disableCombineDocs: boolean;
  disableDownload: boolean;
  documents: IStandardDocumentVersionData[];
  onClick: () => void;
  onDownloadClick: () => void;
  onModalClose: () => void;
  onGenerateAffidavits: (party: ePartyPurpose) => void;
  affidavitGenerating: boolean;
  affidavitGeneratingFailed: boolean;
  transactionType: eTransactionCategory;
  showAffidavitResultModal: boolean;
}

const StyledFlexRow = styled(FlexRow)`
  & > *:first-child {
    margin-left: 5px;
  }
  & > *:last-child {
    margin-left: auto;
  }
`;

const documentGroups = ['All', 'Signing Only', 'Funding Only'] as const;
type DocumentGroup = (typeof documentGroups)[number];

export const SelectDocuments: React.FC<ISelectDocumentsProps> = ({
  affidavitGenerating,
  affidavitGeneratingFailed,
  disableCombineDocs,
  disableDownload,
  documents,
  onClick,
  onDownloadClick,
  onGenerateAffidavits,
  onModalClose,
  searchValue,
  setSearchValue,
  setShowAddDocumentModal,
  showAffidavitResultModal,
  transactionType,
}) => {
  const selectGroup = (group: DocumentGroup) => {
    formik.setFieldTouched('selectedDocuments');
    switch (group) {
      case 'All':
        if (documents.length > formik.values.selectedDocuments.length) {
          // Some documents are not selected
          formik.setFieldValue('selectedDocuments', documents.slice(0));
        } else {
          // All documents are selected, so select none
          formik.setFieldValue('selectedDocuments', []);
        }
        break;
      case 'Signing Only':
        formik.setFieldValue(
          'selectedDocuments',
          documents.filter((d) => d.requiredAtSigning)
        );
        break;
      case 'Funding Only':
        formik.setFieldValue(
          'selectedDocuments',
          documents.filter((d) => d.funding)
        );
        break;
    }
    setMenuVisible(false);
  };

  const dropdownButtonItems: TDropdownElementProps[] = [
    {
      children: (
        <>
          <FontAwesomeIcon icon={faCirclePlus} /> Add file from computer
        </>
      ),
      onClick: () => setShowAddDocumentModal(true),
    },
  ];

  if (transactionType === eTransactionCategory.Purchase) {
    dropdownButtonItems.push({
      name: 'Add Affidavits',
      items: [
        {
          children: (
            <>
              <FontAwesomeIcon icon={faCirclePlus} /> Buyer
            </>
          ),
          onClick: () => onGenerateAffidavits(ePartyPurpose.buyer),
        },
        {
          children: (
            <>
              <FontAwesomeIcon icon={faCirclePlus} /> Seller
            </>
          ),
          onClick: () => onGenerateAffidavits(ePartyPurpose.seller),
        },
      ],
    });
  } else {
    dropdownButtonItems.push({
      children: (
        <>
          <FontAwesomeIcon icon={faCirclePlus} /> Add borrower affidavit
        </>
      ),
      onClick: () => onGenerateAffidavits(ePartyPurpose.borrower),
    });
  }
  const [menuVisible, setMenuVisible] = useState<boolean>(false);
  const formik = useFormikContext<IMergeDocumentValues>();

  return (
    <>
      <StyledFlexRow justifyContent={'start'}>
        <CheckboxInput
          value={formik.values.selectedDocuments.length === documents.length}
          onChange={() => selectGroup('All')}
        />
        <Tooltip
          tooltipShown={menuVisible}
          tooltipContent={
            <OutsideClickWrapper onOutsideClick={() => setMenuVisible(false)}>
              {documentGroups.map((group, index) => (
                <DropdownItem key={index} onClick={() => selectGroup(group)}>
                  {group}
                </DropdownItem>
              ))}
            </OutsideClickWrapper>
          }
        >
          <Button intent="ghost" isInline type="button" onClick={() => setMenuVisible(!menuVisible)}>
            {menuVisible ? <FontAwesomeIcon icon={faCaretUp} /> : <FontAwesomeIcon icon={faCaretDown} />}
          </Button>
        </Tooltip>
        <PineconeThemeProvider>
          <TextInput
            placeholder={'Search'}
            prependIconType={IconType.Search}
            value={searchValue}
            onChange={(option) => option && setSearchValue(option.trim())}
            onBlur={(e) => e.target.value === '' && setSearchValue(null)}
          />
        </PineconeThemeProvider>
        <Button intent="ghost" type="button" isInline onClick={onDownloadClick} disabled={disableDownload}>
          <FontAwesomeIcon icon={faDownload} />
        </Button>
        <Tooltip
          tooltipContent={<BodyText>The document must be a PDF, JPG, JPEG, or PNG to combine</BodyText>}
          trigger={'hover'}
          placement={'right'}
          offset={'.3rem'}
        >
          <Button
            intent="ghost"
            type="button"
            isInline
            title="Combine Documents"
            onClick={onClick}
            disabled={disableCombineDocs}
          >
            <FontAwesomeIcon icon={faFolderPlus} />
          </Button>
        </Tooltip>
        <ButtonDropdown intent={'primary'} items={dropdownButtonItems}>
          + Add Document
        </ButtonDropdown>
      </StyledFlexRow>
      <PageLoading isLoading={affidavitGenerating} />
      {showAffidavitResultModal && (
        <AffidavitResultModal affidavitGeneratingFailed={affidavitGeneratingFailed} onModalClose={onModalClose} />
      )}
    </>
  );
};
